import { encounterField, setEncounterField, type EncounterArr } from '../../shared/array-types';
import { lightTypeName, weatherTypeName } from '../../shared/env-types';
import type { AppliedFrame, AppliedTouchedRow } from '../transport/applied-frame';

type Row = Record<string, unknown>;
const record = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
export type EnvironmentField = 'encounter_light' | 'encounter_weather';

/** One saved catalog choice, without any creature, inventory or computed work. */
export function encounterEnvironmentOnlyRow(frame: AppliedFrame): AppliedTouchedRow | null {
    if (!frame.operation_id.startsWith('encounter-environment:') || frame.rows.length !== 1
        || frame.new_entities.length || frame.deletes.length || frame.new_inventory?.length || frame.inputs
        || Object.keys(frame.member_revs).length || Object.keys(frame.computed).length
        || frame.computed_delta && Object.keys(frame.computed_delta).length) return null;
    const row = frame.rows[0]!;
    const keys = Object.keys(row.row).filter(key => key !== 'encounter_id');
    return row.table === 'encounters' && row.partial === true && upper(row.row_id) === upper(frame.encounter_id)
        && upper(encounterField(row.row, 'encounter_id')) === upper(frame.encounter_id) && keys.length === 1
        && ['encounter_light', 'encounter_weather'].includes(keys[0]!)
        && row.base === undefined && row.node === undefined && row.position === undefined ? row : null;
}

/** Copy only the changed column's shell ancestors; the compiled roster stays held. */
export function patchEncounterEnvironmentRow(source: unknown, row: AppliedTouchedRow, raw: boolean): unknown {
    if (!record(source) || !record(source.encounters)
        || upper(encounterField(source, 'encounter_id')) !== upper(row.row_id)) return source;
    const field: EnvironmentField = Object.prototype.hasOwnProperty.call(row.row, 'encounter_light') ? 'encounter_light' : 'encounter_weather';
    const value = encounterField<string>(row.row, field) ?? null;
    if (String(encounterField(source, field) ?? '') === String(value ?? '')) return source;
    const bucket = { ...source.encounters };
    const next = { ...source, encounters: bucket };
    if (raw) {
        if (value === null) delete bucket[field]; else bucket[field] = value;
    } else {
        const shell = bucket[field];
        if (record(shell) && record(shell.items)) bucket[field] = { ...shell, items: { ...shell.items } };
        const id = value == null || value === '' ? null : String(value);
        setEncounterField(next as EncounterArr, field, id,
            (field === 'encounter_light' ? lightTypeName(id) : weatherTypeName(id)) || undefined);
    }
    return next;
}
